/**
 * RapidIntake — /intake
 *
 * Public route (no auth), same treatment as /dashboards-preview.
 * Three-tab form: My Quota | Closed Business | Active Pipeline
 * Persists to localStorage key: aip:rapid-intake:v1
 * Data feeds back into AE / Manager / Executive dashboard views on reload.
 *
 * Tab 1 — Quota: three input modes (Annual / Quarterly / Monthly)
 *   Default: Quarterly. Even distribution per quarter into monthly.
 * Tab 2 — Closed Business: repeating deal cards
 * Tab 3 — Active Pipeline: opportunity rows with weighted forecast
 */

import { useEffect, useRef, useState, useCallback } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { Link } from "wouter";
import { INPUT_LIMITS, safeTruncate } from "@/lib/input-limits";
import {
  loadStore, saveStore,
  upsertQuota, addClosedDeal, updateClosedDeal, deleteClosedDeal,
  addPipelineOpp, updatePipelineOpp, deletePipelineOpp,
  totalContractValue, weightedForecast, totalWeightedForecast,
  deriveMonthlyFromAnnual, deriveMonthlyFromQuarterly, annualFromMonthly,
  downloadJson, fmt$, newId,
  MONTH_LABELS, AE_OPTIONS, PRODUCT_OPTIONS, STAGE_LABELS, STAGE_MULTIPLIERS, STAGE_KEYS,
  type IntakeStore, type QuotaEntry, type QuotaInputMode,
  type ClosedDeal, type PipelineOpp, type StageKey, type ProductOption, type AeName,
} from "@/lib/intake-store";

// ─── Shared input styles ──────────────────────────────────────────────────────

const INPUT  = "w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:border-[#0B0B0F] focus:ring-2 focus:ring-neutral-200 outline-none transition-all";
const SELECT = `${INPUT} cursor-pointer`;
const LABEL  = "block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5";
const BTN_PRIMARY = "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B0B0F] hover:bg-[#374151] text-white text-sm font-semibold transition-all shadow-sm";
const BTN_GHOST   = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600 hover:bg-neutral-50 transition-all";
const BTN_DANGER  = "inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-rose-500 hover:bg-rose-50 transition-all";

function currency(n: number) {
  return n ? `$${Number(n).toLocaleString()}` : "";
}

// ─── Save pulse animation helper ─────────────────────────────────────────────

function SaveFlash({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-1 text-neutral-600 text-xs font-semibold animate-pulse">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="6" stroke="#0B0B0F" strokeWidth="1.5" />
        <path d="M4.5 7l2 2 3-3" stroke="#0B0B0F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Saved
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// TAB 1 — MY QUOTA
// ════════════════════════════════════════════════════════════════════

function QuotaTab({ store, onUpdate }: { store: IntakeStore; onUpdate: (s: IntakeStore) => void }) {
  const [aeName, setAeName]     = useState<AeName>(AE_OPTIONS[0]);
  const [customAe, setCustomAe] = useState("");
  const [mode, setMode]         = useState<QuotaInputMode>("quarterly");
  const [year, setYear]         = useState(new Date().getFullYear());

  // Annual input
  const [annualRaw, setAnnualRaw] = useState("");

  // Quarterly inputs
  const [qtrs, setQtrs] = useState<string[]>(["", "", "", ""]);

  // Monthly inputs
  const [months, setMonths] = useState<string[]>(Array(12).fill(""));

  // Derived monthly preview (for annual/quarterly modes)
  const [derivedMonthly, setDerivedMonthly] = useState<number[]>(Array(12).fill(0));

  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Recalculate derived monthly whenever inputs change
  useEffect(() => {
    if (mode === "annual") {
      const v = parseFloat(annualRaw.replace(/[^0-9.]/g, "")) || 0;
      setDerivedMonthly(deriveMonthlyFromAnnual(v) as number[]);
    } else if (mode === "quarterly") {
      const q = qtrs.map((s) => parseFloat(s.replace(/[^0-9.]/g, "")) || 0) as [number,number,number,number];
      setDerivedMonthly(deriveMonthlyFromQuarterly(q) as number[]);
    }
  }, [mode, annualRaw, qtrs]);

  // Load existing quota for selected AE
  useEffect(() => {
    const resolvedName = aeName === "Add new" ? customAe : aeName;
    const existing = store.quotas.find((q) => q.aeName === resolvedName);
    if (!existing) return;
    setMode(existing.quotaInputMode);
    setYear(existing.effectiveYear);
    if (existing.quotaInputMode === "annual" && existing.quotaAnnual) {
      setAnnualRaw(String(existing.quotaAnnual));
    } else if (existing.quotaInputMode === "quarterly" && existing.quotaQuarterly) {
      setQtrs(existing.quotaQuarterly.map(String));
    } else {
      setMonths(existing.quotaMonthly.map(String));
    }
  }, [aeName, store.quotas]);

  function handleSave() {
    const resolvedName = aeName === "Add new" ? customAe.trim() : aeName;
    if (!resolvedName) return;

    let monthly: QuotaEntry["quotaMonthly"];
    let annualVal: number | null = null;
    let qtrVal: [number,number,number,number] | null = null;

    if (mode === "annual") {
      annualVal = parseFloat(annualRaw.replace(/[^0-9.]/g, "")) || 0;
      monthly = deriveMonthlyFromAnnual(annualVal);
    } else if (mode === "quarterly") {
      qtrVal = qtrs.map((s) => parseFloat(s.replace(/[^0-9.]/g, "")) || 0) as [number,number,number,number];
      monthly = deriveMonthlyFromQuarterly(qtrVal);
      annualVal = qtrVal.reduce((s, v) => s + v, 0);
    } else {
      monthly = months.map((s) => parseFloat(s.replace(/[^0-9.]/g, "")) || 0) as QuotaEntry["quotaMonthly"];
      annualVal = monthly.reduce((s, v) => s + v, 0);
    }

    const entry: QuotaEntry = {
      aeId: resolvedName.toLowerCase().replace(/[^a-z0-9]/g, "-"),
      aeName: resolvedName,
      effectiveYear: year,
      quotaInputMode: mode,
      quotaAnnual: annualVal,
      quotaQuarterly: qtrVal,
      quotaMonthly: monthly,
      savedAt: Date.now(),
    };

    const next = upsertQuota(store, entry);
    saveStore(next);
    onUpdate(next);
    setSaved(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 2500);
  }

  const annualNumeric = (() => {
    if (mode === "annual") return parseFloat(annualRaw.replace(/[^0-9.]/g, "")) || 0;
    if (mode === "quarterly") return qtrs.reduce((s, v) => s + (parseFloat(v.replace(/[^0-9.]/g, "")) || 0), 0);
    return months.reduce((s, v) => s + (parseFloat(v.replace(/[^0-9.]/g, "")) || 0), 0);
  })();

  const modeToggleClass = (m: QuotaInputMode) =>
    `px-4 py-2 text-sm font-semibold rounded-lg border transition-all ${
      mode === m
        ? "bg-[#0B0B0F] text-white border-[#0B0B0F] shadow-sm"
        : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
    }`;

  return (
    <div className="space-y-7 max-w-2xl">
      {/* AE selection */}
      <div>
        <label className={LABEL}>Your Name</label>
        <select className={SELECT} value={aeName} onChange={(e) => setAeName(e.target.value as AeName)}>
          {AE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value="Add new">Add new…</option>
        </select>
        {aeName === "Add new" && (
          <input className={`${INPUT} mt-2`} placeholder="Your name" value={customAe}
            maxLength={INPUT_LIMITS.name}
            onChange={(e) => setCustomAe(safeTruncate(e.target.value, INPUT_LIMITS.name))} />
        )}
      </div>

      <div className="flex gap-4 items-end flex-wrap">
        <div className="flex-1 min-w-[120px]">
          <label className={LABEL}>Quota Year</label>
          <select className={SELECT} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Annual preview */}
        {annualNumeric > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-0.5">Annual Quota</div>
            <div className="text-3xl font-black text-[#0B0B0F] tabular-nums">{fmt$(annualNumeric)}</div>
          </div>
        )}
      </div>

      {/* Mode toggle */}
      <div>
        <div className="flex gap-2 flex-wrap mb-2">
          {(["annual", "quarterly", "monthly"] as QuotaInputMode[]).map((m) => (
            <button key={m} className={modeToggleClass(m)} onClick={() => setMode(m)}>
              {m === "annual" ? "Annual" : m === "quarterly" ? "Quarterly (default)" : "Monthly"}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500">
          Most reps work in quarterly targets — pick whichever matches how you plan.
        </p>
      </div>

      {/* ── Annual mode ── */}
      {mode === "annual" && (
        <div>
          <label className={LABEL}>Annual Quota ($)</label>
          <input
            className={INPUT}
            placeholder="e.g. 1200000"
            maxLength={30}
            value={annualRaw}
            onChange={(e) => setAnnualRaw(safeTruncate(e.target.value, 30))}
          />
          {annualNumeric > 0 && (
            <div className="mt-4 p-4 rounded-xl bg-neutral-50 border border-neutral-200">
              <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                Monthly view (auto-derived — annual ÷ 12)
              </div>
              <div className="grid grid-cols-6 gap-2">
                {derivedMonthly.map((v, i) => (
                  <div key={i} className="text-center">
                    <div className="text-[9px] text-neutral-500 uppercase">{MONTH_LABELS[i]}</div>
                    <div className="text-xs font-bold text-neutral-700 tabular-nums">{fmt$(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Quarterly mode (default) ── */}
      {mode === "quarterly" && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
              <div key={q}>
                <label className={LABEL}>{q} Target ($)</label>
                <input
                  className={INPUT}
                  placeholder="e.g. 300000"
                  maxLength={30}
                  value={qtrs[i]}
                  onChange={(e) => {
                    const next = [...qtrs];
                    next[i] = safeTruncate(e.target.value, 30);
                    setQtrs(next);
                  }}
                />
                {qtrs[i] && (
                  <div className="text-[10px] text-neutral-600 font-semibold mt-1">
                    {fmt$(parseFloat(qtrs[i].replace(/[^0-9.]/g, "")) || 0)} / qtr
                  </div>
                )}
              </div>
            ))}
          </div>

          {qtrs.some((v) => parseFloat(v) > 0) && (
            <div className="mt-4 p-4 rounded-xl bg-neutral-50 border border-neutral-200">
              <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                Monthly view (auto-derived — each quarter distributed evenly across its 3 months)
              </div>
              <div className="grid grid-cols-6 gap-2">
                {derivedMonthly.map((v, i) => (
                  <div key={i} className={`text-center rounded-lg py-1.5 ${[0,3,6,9].includes(i) ? "bg-neutral-200" : ""}`}>
                    <div className="text-[9px] text-neutral-500 uppercase">{MONTH_LABELS[i]}</div>
                    <div className="text-xs font-bold text-neutral-700 tabular-nums">{fmt$(v)}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-neutral-500 mt-2">
                Highlighted months = first month of each quarter (receives any rounding remainder)
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Monthly mode ── */}
      {mode === "monthly" && (
        <div>
          <div className="text-xs text-neutral-500 mb-3">
            Monthly — only if you have month-by-month numbers.
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {MONTH_LABELS.map((mo, i) => (
              <div key={mo}>
                <label className={LABEL}>{mo}</label>
                <input
                  className={INPUT}
                  placeholder="e.g. 100000"
                  maxLength={30}
                  value={months[i]}
                  onChange={(e) => {
                    const next = [...months];
                    next[i] = safeTruncate(e.target.value, 30);
                    setMonths(next);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button className={BTN_PRIMARY} onClick={handleSave}>
          Save Quota
        </button>
        <SaveFlash show={saved} />
        <button className={BTN_GHOST}
          onClick={() => downloadJson(store.quotas, "aip-quota-data.json")}>
          Download JSON
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TAB 2 — CLOSED BUSINESS
// ════════════════════════════════════════════════════════════════════

const BLANK_DEAL = (): Omit<ClosedDeal, "id" | "savedAt"> => ({
  dateClosed: new Date().toISOString().slice(0, 10),
  accountName: "",
  product: "Multi-module Bundle",
  dealType: "New",
  oneTimeAmount: 0,
  recurringAmount: 0,
  recurringPeriod: "month",
  contractLengthMonths: 12,
});

function DealCard({
  deal, onUpdate, onDelete,
}: {
  deal: ClosedDeal;
  onUpdate: (d: ClosedDeal) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ClosedDeal>(deal);
  const tcv = totalContractValue(draft);

  function field<K extends keyof ClosedDeal>(key: K, val: ClosedDeal[K]) {
    setDraft((d) => ({ ...d, [key]: val }));
  }

  if (!editing) {
    return (
      <div className="flex items-start gap-4 p-4 rounded-xl bg-neutral-50 border border-neutral-200">
        <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm">
          <div><span className="text-[9px] uppercase tracking-wide text-neutral-500 block">Account</span><span className="font-semibold text-neutral-800">{deal.accountName}</span></div>
          <div><span className="text-[9px] uppercase tracking-wide text-neutral-500 block">Product</span><span className="text-neutral-600">{deal.product}</span></div>
          <div><span className="text-[9px] uppercase tracking-wide text-neutral-500 block">Closed</span><span className="text-neutral-600">{deal.dateClosed}</span></div>
          <div><span className="text-[9px] uppercase tracking-wide text-neutral-500 block">TCV</span><span className="font-bold text-[#0B0B0F] tabular-nums">{currency(Math.round(totalContractValue(deal)))}</span></div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button className={BTN_GHOST} onClick={() => setEditing(true)}>Edit</button>
          <button className={BTN_DANGER} onClick={onDelete}>Remove</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 rounded-xl border-2 border-neutral-300 bg-neutral-50 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL}>Account Name</label>
          <input className={INPUT} value={draft.accountName}
            maxLength={INPUT_LIMITS.name}
            onChange={(e) => field("accountName", safeTruncate(e.target.value, INPUT_LIMITS.name))} placeholder="Account name" />
        </div>
        <div>
          <label className={LABEL}>Date Closed</label>
          <input type="date" className={INPUT} value={draft.dateClosed}
            onChange={(e) => field("dateClosed", e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>Product</label>
          <select className={SELECT} value={draft.product}
            onChange={(e) => field("product", e.target.value as ProductOption)}>
            {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>Deal Type</label>
          <select className={SELECT} value={draft.dealType}
            onChange={(e) => field("dealType", e.target.value as "New" | "Expansion" | "Renewal")}>
            {["New", "Expansion", "Renewal"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>One-time Amount ($)</label>
          <input type="number" className={INPUT} value={draft.oneTimeAmount || ""}
            onChange={(e) => field("oneTimeAmount", parseFloat(e.target.value) || 0)}
            placeholder="0" min="0" />
        </div>
        <div>
          <label className={LABEL}>Recurring Amount ($)</label>
          <div className="flex gap-2">
            <input type="number" className={`${INPUT} flex-1`} value={draft.recurringAmount || ""}
              onChange={(e) => field("recurringAmount", parseFloat(e.target.value) || 0)}
              placeholder="0" min="0" />
            <select className="rounded-lg border border-neutral-200 bg-white px-2 py-2.5 text-sm focus:border-[#0B0B0F] outline-none"
              value={draft.recurringPeriod}
              onChange={(e) => field("recurringPeriod", e.target.value as "month" | "year")}>
              <option value="month">/mo</option>
              <option value="year">/yr</option>
            </select>
          </div>
        </div>
        <div>
          <label className={LABEL}>Contract Length (months)</label>
          <input type="number" className={INPUT} value={draft.contractLengthMonths}
            onChange={(e) => field("contractLengthMonths", parseInt(e.target.value) || 12)}
            placeholder="12" min="1" />
        </div>
        <div className="flex flex-col justify-end">
          <div className="p-3 rounded-lg bg-white border border-neutral-200 text-center">
            <div className="text-[9px] uppercase tracking-wide text-neutral-500 mb-0.5">Total Contract Value</div>
            <div className="text-2xl font-black text-[#0B0B0F] tabular-nums">{currency(Math.round(tcv))}</div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button className={BTN_PRIMARY} onClick={() => { onUpdate({ ...draft, savedAt: Date.now() }); setEditing(false); }}>
          Save Deal
        </button>
        <button className={BTN_GHOST} onClick={() => { setDraft(deal); setEditing(false); }}>Cancel</button>
      </div>
    </div>
  );
}

function ClosedTab({ store, onUpdate }: { store: IntakeStore; onUpdate: (s: IntakeStore) => void }) {
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState(BLANK_DEAL());
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  const tcv = totalContractValue({ ...newDraft, id: "", savedAt: 0 });

  function field<K extends keyof typeof newDraft>(key: K, val: (typeof newDraft)[K]) {
    setNewDraft((d) => ({ ...d, [key]: val }));
  }

  function handleAdd() {
    if (!newDraft.accountName.trim()) return;
    const deal: ClosedDeal = { ...newDraft, id: newId(), savedAt: Date.now() };
    const next = addClosedDeal(store, deal);
    saveStore(next);
    onUpdate(next);
    setNewDraft(BLANK_DEAL());
    setAdding(false);
    setSaved(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 2500);
  }

  const total = store.closedDeals.reduce((s, d) => s + totalContractValue(d), 0);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Running total */}
      {store.closedDeals.length > 0 && (
        <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Total Closed Value ({store.closedDeals.length} deal{store.closedDeals.length !== 1 ? "s" : ""})</div>
            <div className="text-3xl font-black text-[#0B0B0F] tabular-nums">{fmt$(total)}</div>
          </div>
          <button className={BTN_GHOST}
            onClick={() => downloadJson(store.closedDeals, "aip-closed-deals.json")}>
            Download JSON
          </button>
        </div>
      )}

      {/* Existing deals */}
      {store.closedDeals.map((deal) => (
        <DealCard
          key={deal.id}
          deal={deal}
          onUpdate={(updated) => {
            const next = updateClosedDeal(store, updated);
            saveStore(next);
            onUpdate(next);
          }}
          onDelete={() => {
            const next = deleteClosedDeal(store, deal.id);
            saveStore(next);
            onUpdate(next);
          }}
        />
      ))}

      {/* Add new deal form */}
      {adding && (
        <div className="p-5 rounded-xl border-2 border-neutral-300 bg-neutral-50 space-y-4">
          <div className="font-semibold text-neutral-700 text-sm">New Closed Deal</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Account Name</label>
              <input className={INPUT} value={newDraft.accountName}
                maxLength={INPUT_LIMITS.name}
                onChange={(e) => field("accountName", safeTruncate(e.target.value, INPUT_LIMITS.name))} placeholder="Account name" autoFocus />
            </div>
            <div>
              <label className={LABEL}>Date Closed</label>
              <input type="date" className={INPUT} value={newDraft.dateClosed}
                onChange={(e) => field("dateClosed", e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Product</label>
              <select className={SELECT} value={newDraft.product}
                onChange={(e) => field("product", e.target.value as ProductOption)}>
                {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Deal Type</label>
              <select className={SELECT} value={newDraft.dealType}
                onChange={(e) => field("dealType", e.target.value as "New" | "Expansion" | "Renewal")}>
                {["New", "Expansion", "Renewal"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>One-time Amount ($)</label>
              <input type="number" className={INPUT} value={newDraft.oneTimeAmount || ""}
                onChange={(e) => field("oneTimeAmount", parseFloat(e.target.value) || 0)}
                placeholder="0" min="0" />
            </div>
            <div>
              <label className={LABEL}>Recurring Amount ($)</label>
              <div className="flex gap-2">
                <input type="number" className={`${INPUT} flex-1`} value={newDraft.recurringAmount || ""}
                  onChange={(e) => field("recurringAmount", parseFloat(e.target.value) || 0)}
                  placeholder="0" min="0" />
                <select className="rounded-lg border border-neutral-200 bg-white px-2 py-2.5 text-sm focus:border-[#0B0B0F] outline-none"
                  value={newDraft.recurringPeriod}
                  onChange={(e) => field("recurringPeriod", e.target.value as "month" | "year")}>
                  <option value="month">/mo</option>
                  <option value="year">/yr</option>
                </select>
              </div>
            </div>
            <div>
              <label className={LABEL}>Contract Length (months)</label>
              <input type="number" className={INPUT} value={newDraft.contractLengthMonths}
                onChange={(e) => field("contractLengthMonths", parseInt(e.target.value) || 12)}
                placeholder="12" min="1" />
            </div>
            <div className="flex flex-col justify-end">
              <div className="p-3 rounded-lg bg-white border border-neutral-200 text-center">
                <div className="text-[9px] uppercase tracking-wide text-neutral-500 mb-0.5">Total Contract Value</div>
                <div className="text-2xl font-black text-[#0B0B0F] tabular-nums">{currency(Math.round(tcv))}</div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className={BTN_PRIMARY} onClick={handleAdd}>Save Deal</button>
            <button className={BTN_GHOST} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {!adding && (
          <button className={BTN_PRIMARY} onClick={() => setAdding(true)}>
            + Add Closed Deal
          </button>
        )}
        <SaveFlash show={saved} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TAB 3 — ACTIVE PIPELINE
// ════════════════════════════════════════════════════════════════════

const BLANK_OPP = (): Omit<PipelineOpp, "id" | "savedAt"> => ({
  accountName: "",
  product: "Multi-module Bundle",
  stage: "CP3",
  expectedCloseDate: "",
  dealSize: 0,
  recurringAmount: 0,
  scorePct: Math.round(STAGE_MULTIPLIERS["CP3"] * 100),
});

function OppRow({
  opp, onUpdate, onDelete,
}: {
  opp: PipelineOpp;
  onUpdate: (o: PipelineOpp) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PipelineOpp>(opp);
  const wf = weightedForecast(draft);

  function field<K extends keyof PipelineOpp>(key: K, val: PipelineOpp[K]) {
    const next = { ...draft, [key]: val };
    // Auto-sync score to stage default if stage changes
    if (key === "stage") {
      next.scorePct = Math.round(STAGE_MULTIPLIERS[val as StageKey] * 100);
    }
    setDraft(next);
  }

  if (!editing) {
    return (
      <tr className="hover:bg-neutral-50/60 transition-colors border-b border-neutral-50">
        <td className="px-4 py-3 font-semibold text-neutral-800 text-sm">{opp.accountName}</td>
        <td className="px-3 py-3 text-xs text-neutral-500">{opp.product.replace("Partner — ", "")}</td>
        <td className="px-3 py-3">
          <span className="text-xs font-bold text-[#0B0B0F] bg-neutral-100 px-2 py-0.5 rounded">{opp.stage}</span>
        </td>
        <td className="px-3 py-3 text-xs text-neutral-500">{opp.expectedCloseDate || "—"}</td>
        <td className="px-3 py-3 text-sm font-medium tabular-nums text-neutral-700">{currency(opp.dealSize)}</td>
        <td className="px-3 py-3 text-sm text-neutral-500 tabular-nums">{opp.scorePct}%</td>
        <td className="px-3 py-3 text-sm font-bold text-[#0B0B0F] tabular-nums">{currency(Math.round(weightedForecast(opp)))}</td>
        <td className="px-3 py-3">
          <div className="flex gap-1">
            <button className={BTN_GHOST} onClick={() => setEditing(true)}>Edit</button>
            <button className={BTN_DANGER} onClick={onDelete}>×</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-neutral-50 border-b-2 border-neutral-300">
      <td colSpan={8} className="px-4 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div>
            <label className={LABEL}>Account Name</label>
            <input className={INPUT} value={draft.accountName}
              maxLength={INPUT_LIMITS.name}
              onChange={(e) => field("accountName", safeTruncate(e.target.value, INPUT_LIMITS.name))} autoFocus />
          </div>
          <div>
            <label className={LABEL}>Product</label>
            <select className={SELECT} value={draft.product}
              onChange={(e) => field("product", e.target.value as ProductOption)}>
              {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p.replace("Partner — ", "")}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Stage</label>
            <select className={SELECT} value={draft.stage}
              onChange={(e) => field("stage", e.target.value as StageKey)}>
              {STAGE_KEYS.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Expected Close</label>
            <input type="date" className={INPUT} value={draft.expectedCloseDate}
              onChange={(e) => field("expectedCloseDate", e.target.value)} />
          </div>
          <div>
            <label className={LABEL}>Deal Size ($)</label>
            <input type="number" className={INPUT} value={draft.dealSize || ""}
              onChange={(e) => field("dealSize", parseFloat(e.target.value) || 0)} placeholder="0" min="0" />
          </div>
          <div>
            <label className={LABEL}>Recurring ($/mo)</label>
            <input type="number" className={INPUT} value={draft.recurringAmount || ""}
              onChange={(e) => field("recurringAmount", parseFloat(e.target.value) || 0)} placeholder="0" min="0" />
          </div>
          <div>
            <label className={LABEL}>Score % (0–100)</label>
            <div className="flex items-center gap-3">
              <input type="range" min={0} max={100} value={draft.scorePct}
                onChange={(e) => field("scorePct", parseInt(e.target.value))}
                className="flex-1 accent-[#0B0B0F]" />
              <span className="text-sm font-bold text-[#0B0B0F] tabular-nums w-8 text-right">{draft.scorePct}</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Stage default: {Math.round(STAGE_MULTIPLIERS[draft.stage] * 100)}%
            </div>
          </div>
          <div className="flex flex-col justify-end">
            <div className="p-3 rounded-lg bg-white border border-neutral-200 text-center">
              <div className="text-[9px] uppercase tracking-wide text-neutral-500 mb-0.5">Weighted Forecast</div>
              <div className="text-xl font-black text-[#0B0B0F] tabular-nums">{currency(Math.round(wf))}</div>
              <div className="text-[9px] text-neutral-500 mt-0.5">
                {currency(draft.dealSize)} × {draft.scorePct}% × {Math.round(STAGE_MULTIPLIERS[draft.stage]*100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className={BTN_PRIMARY} onClick={() => { onUpdate({ ...draft, savedAt: Date.now() }); setEditing(false); }}>
            Save
          </button>
          <button className={BTN_GHOST} onClick={() => { setDraft(opp); setEditing(false); }}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}

function PipelineTab({ store, onUpdate }: { store: IntakeStore; onUpdate: (s: IntakeStore) => void }) {
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState(BLANK_OPP());
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  const total = totalWeightedForecast(store.pipeline);
  const wfNew = weightedForecast({ ...newDraft, id: "", savedAt: 0 });

  function nfield<K extends keyof typeof newDraft>(key: K, val: (typeof newDraft)[K]) {
    const next = { ...newDraft, [key]: val };
    if (key === "stage") next.scorePct = Math.round(STAGE_MULTIPLIERS[val as StageKey] * 100);
    setNewDraft(next);
  }

  function handleAdd() {
    if (!newDraft.accountName.trim()) return;
    const opp: PipelineOpp = { ...newDraft, id: newId(), savedAt: Date.now() };
    const next = addPipelineOpp(store, opp);
    saveStore(next);
    onUpdate(next);
    setNewDraft(BLANK_OPP());
    setAdding(false);
    setSaved(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div className="space-y-5">
      {/* Running weighted total */}
      <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">
            Total Weighted Forecast ({store.pipeline.length} opp{store.pipeline.length !== 1 ? "s" : ""})
          </div>
          <div className="text-3xl font-black text-[#0B0B0F] tabular-nums transition-all">
            {fmt$(total)}
          </div>
        </div>
        <button className={BTN_GHOST}
          onClick={() => downloadJson(store.pipeline, "aip-pipeline.json")}>
          Download JSON
        </button>
      </div>

      {/* Pipeline table */}
      {store.pipeline.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                {["Account", "Product", "Stage", "Close Date", "Deal Size", "Score", "Weighted $", ""].map((h) => (
                  <th key={h} className="text-left text-[9px] uppercase tracking-wide text-neutral-500 font-semibold px-3 sm:px-4 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {store.pipeline.map((opp) => (
                <OppRow
                  key={opp.id}
                  opp={opp}
                  onUpdate={(updated) => {
                    const next = updatePipelineOpp(store, updated);
                    saveStore(next);
                    onUpdate(next);
                  }}
                  onDelete={() => {
                    const next = deletePipelineOpp(store, opp.id);
                    saveStore(next);
                    onUpdate(next);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new opp inline form */}
      {adding && (
        <div className="p-5 rounded-xl border-2 border-neutral-300 bg-neutral-50 space-y-4">
          <div className="font-semibold text-neutral-700 text-sm">New Opportunity</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <label className={LABEL}>Account Name</label>
              <input className={INPUT} value={newDraft.accountName}
                maxLength={INPUT_LIMITS.name}
                onChange={(e) => nfield("accountName", safeTruncate(e.target.value, INPUT_LIMITS.name))}
                placeholder="Account name" autoFocus />
            </div>
            <div>
              <label className={LABEL}>Product</label>
              <select className={SELECT} value={newDraft.product}
                onChange={(e) => nfield("product", e.target.value as ProductOption)}>
                {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p.replace("Partner — ", "")}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Stage</label>
              <select className={SELECT} value={newDraft.stage}
                onChange={(e) => nfield("stage", e.target.value as StageKey)}>
                {STAGE_KEYS.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Expected Close</label>
              <input type="date" className={INPUT} value={newDraft.expectedCloseDate}
                onChange={(e) => nfield("expectedCloseDate", e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Deal Size ($)</label>
              <input type="number" className={INPUT} value={newDraft.dealSize || ""}
                onChange={(e) => nfield("dealSize", parseFloat(e.target.value) || 0)}
                placeholder="0" min="0" />
            </div>
            <div>
              <label className={LABEL}>Recurring ($/mo)</label>
              <input type="number" className={INPUT} value={newDraft.recurringAmount || ""}
                onChange={(e) => nfield("recurringAmount", parseFloat(e.target.value) || 0)}
                placeholder="0" min="0" />
            </div>
            <div>
              <label className={LABEL}>Score % (0–100)</label>
              <div className="flex items-center gap-3">
                <input type="range" min={0} max={100} value={newDraft.scorePct}
                  onChange={(e) => nfield("scorePct", parseInt(e.target.value))}
                  className="flex-1 accent-[#0B0B0F]" />
                <span className="text-sm font-bold text-[#0B0B0F] tabular-nums w-8 text-right">{newDraft.scorePct}</span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                Stage default: {Math.round(STAGE_MULTIPLIERS[newDraft.stage] * 100)}%
              </div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="p-3 rounded-lg bg-white border border-neutral-200 text-center">
                <div className="text-[9px] uppercase tracking-wide text-neutral-500 mb-0.5">Weighted Forecast</div>
                <div className="text-xl font-black text-[#0B0B0F] tabular-nums">{currency(Math.round(wfNew))}</div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className={BTN_PRIMARY} onClick={handleAdd}>Save Opportunity</button>
            <button className={BTN_GHOST} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {!adding && (
          <button className={BTN_PRIMARY} onClick={() => setAdding(true)}>
            + Add Opportunity
          </button>
        )}
        <SaveFlash show={saved} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════

const TABS = [
  { id: "quota",    label: "My Quota" },
  { id: "closed",   label: "Closed Business" },
  { id: "pipeline", label: "Active Pipeline" },
] as const;
type TabId = (typeof TABS)[number]["id"];

export default function RapidIntake() {
  const [store, setStore]   = useState<IntakeStore>(() => loadStore());
  const [tab, setTab]       = useState<TabId>("quota");

  // Re-read from localStorage on mount in case another tab wrote to it
  useEffect(() => { setStore(loadStore()); }, []);

  const tabClass = (id: TabId) =>
    `px-5 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
      tab === id
        ? "border-[#0B0B0F] text-[#0B0B0F]"
        : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
    }`;

  const totalClosed   = store.closedDeals.length;
  const totalPipeline = store.pipeline.length;
  const totalQuotas   = store.quotas.length;

  return (
    <OperatorShell breadcrumbs={[{ label: "Rapid Intake" }]} pageTitle="Rapid Intake" pageMeta="Account data entry">

      {/* ── Mi6 brand band ───────────────────────────────────────── */}

      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-14 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link href="/dashboards-preview">
              <a className="text-neutral-500 hover:text-neutral-600 transition-colors" title="Back to dashboards">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M12 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
              <h1 className="text-lg font-bold text-neutral-900 leading-none">Rapid Intake</h1>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-wide border border-[#B91C1C]">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block" />
              Preview Mode
            </span>
          </div>
          <div className="text-xs text-neutral-500">
            Data saves to your browser — download JSON to back up
          </div>
        </div>

        {/* Tab strip — sticky */}
        <div className="max-w-4xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} className={tabClass(t.id)} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === "closed" && totalClosed > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-neutral-200 text-neutral-700 text-[9px] font-bold">{totalClosed}</span>
              )}
              {t.id === "pipeline" && totalPipeline > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-neutral-200 text-neutral-700 text-[9px] font-bold">{totalPipeline}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-4xl mx-auto px-6 py-8">

        {/* Helper text */}
        <div className="mb-6 p-4 rounded-xl bg-neutral-50 border border-neutral-200">
          <div className="text-sm text-neutral-700 font-semibold mb-0.5">How this works</div>
          <div className="text-xs text-neutral-600 leading-relaxed">
            Enter your quota, closed deals, and active pipeline below. Your data saves automatically to this browser.
            Once saved, open the <Link href="/dashboards-preview"><a className="underline font-semibold">Dashboards preview</a></Link> and
            select the AE or Manager view — your numbers will appear in the charts immediately.
            Use "Download JSON" on any tab to capture a backup.
          </div>
        </div>

        {tab === "quota"    && <QuotaTab    store={store} onUpdate={setStore} />}
        {tab === "closed"   && <ClosedTab   store={store} onUpdate={setStore} />}
        {tab === "pipeline" && <PipelineTab store={store} onUpdate={setStore} />}
      </main>

      {/* Bottom status bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-neutral-200 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-xs text-neutral-500 flex-wrap">
            <span>
              <span className="font-bold text-neutral-700">{totalQuotas}</span> quota{totalQuotas !== 1 ? "s" : ""} saved
            </span>
            <span className="text-neutral-300">·</span>
            <span>
              <span className="font-bold text-neutral-700">{totalClosed}</span> closed deal{totalClosed !== 1 ? "s" : ""}
            </span>
            <span className="text-neutral-300">·</span>
            <span>
              <span className="font-bold text-neutral-700">{totalPipeline}</span> pipeline opp{totalPipeline !== 1 ? "s" : ""}
            </span>
            {totalPipeline > 0 && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="font-bold text-[#DC2626]">{fmt$(totalWeightedForecast(store.pipeline))}</span>
                <span className="text-neutral-500">weighted forecast</span>
              </>
            )}
          </div>
          <Link href="/dashboards-preview">
            <a className="text-xs px-3 py-1.5 rounded-lg bg-[#0B0B0F] text-white font-semibold hover:bg-[#374151] transition-colors">
              View in Dashboards
            </a>
          </Link>
        </div>
      </div>

      {/* ── Mi6 brand footer ──────────────────────────────────── */}
    </OperatorShell>
  );
}
